<template>
    <v-navigation-drawer :class="[$vuetify.display.mdAndUp ? 'rounded-xl standard' : 'mobile']" elevation="3"
        mobile-breakpoint="0" :rail="$vuetify.display.smAndUp" v-model:rail="rail" v-model="drawer"
        :expand-on-hover="$vuetify.display.mdAndUp"
        :temporary="$vuetify.display.xs && orientation === 'portrait-primary'">
        <router-link :to="{ name: 'Index' }">
            <v-list-item class="mt-4 px-3" height="55px" v-haptic>
                <Transition name="image-full">
                    <v-img key="full" v-show="!rail || $vuetify.display.xs" height="100%"
                        src="@/assets/logo-full.svg" />
                </Transition>
                <Transition name="image-ne">
                    <v-img key="short" v-show="rail && $vuetify.display.smAndUp" height="100%" width="34px"
                        src="@/assets/logo-NE.svg" />
                </Transition>
            </v-list-item>
        </router-link>
        <v-divider class="mb-1 mb-md-3"></v-divider>
        <v-list-item :to="{ name: 'Dashboard' }" v-haptic height="48px" :class="{ 'ps-3': rail }">
            <template #prepend>
                <v-avatar :size="rail ? 30 : 40">
                    <v-img :src="profilePictureUrl"></v-img>
                </v-avatar>
            </template>
            <v-list-item-title class="font-weight-medium">Bonjour {{ user.firstName }}</v-list-item-title>
        </v-list-item>
        <v-divider class="my-1 my-md-3"></v-divider>
        <v-list-item :to="{ name: 'Messages' }" v-haptic title="Conversations" class="ps-3">
            <template #prepend>
                <v-icon size="large" class="ms-1">$messages</v-icon>
            </template>
            <ConversationNotification class="left-0 ms-8 mt-n7" size="small" :number="messageNotifs">
            </ConversationNotification>
        </v-list-item>
        <v-list-item v-if="user.accountType === 'COMPANY'" :to="{ name: 'MyTenders' }" v-haptic
            title="Mes appels d'offres">
            <template #prepend>
                <v-icon size="large" class="ms-1">$mission</v-icon>
            </template>
        </v-list-item>
        <v-divider class="my-3"></v-divider>
        <v-list-item :to="{ name: 'Pilotes' }" v-haptic title="Pilotes" class="ps-3">
            <template #prepend>
                <v-icon size="large">$pilotes</v-icon>
            </template>
        </v-list-item>
        <v-list-item :to="{ name: 'Tenders' }" v-haptic title="Appels d'offres" class="ps-3">
            <template #prepend>
                <v-icon size="large">fal fa-file-contract</v-icon>
            </template>
        </v-list-item>
        <v-spacer></v-spacer>
        <template #append>
            <template v-if="isAdmin">
                <v-divider></v-divider>
                <v-list-item :to="{ name: 'Manage' }" v-haptic title="Administration" class="ps-3"
                    :class="{ 'ps-10': !rail }">
                    <template #prepend>
                        <v-icon size="large">$admin</v-icon>
                    </template>
                </v-list-item>
            </template>
            <template v-else>
                <v-divider></v-divider>
                <v-list-item v-haptic @click="askForHelp" title="Demander de l'aide" class="ps-3"
                    :class="{ 'ps-10': !rail }">
                    <template #prepend>
                        <v-icon size="large">fal fa-messages-question</v-icon>
                    </template>
                </v-list-item>
            </template>
            <v-divider></v-divider>
            <v-list-item @click="logoutClick" class="rounded-b-xl ps-3" :class="{ 'ps-10': !rail }"
                title="Se déconnecter" v-haptic>
                <template #prepend>
                    <v-icon size="large">$logout</v-icon>
                </template>
            </v-list-item>
        </template>
    </v-navigation-drawer>
    <v-bottom-navigation v-if="$vuetify.display.xs && orientation === 'portrait-primary'" :elevation="2">
        <v-btn value="menu" :active="false" @click="drawer = true" v-haptic.soft>
            <v-icon>fal fa-bars</v-icon>

            <span>Menu</span>
        </v-btn>

        <v-btn value="messages" :to="{ name: 'Messages' }" v-haptic>
            <v-icon>$messages</v-icon>

            <span>Conversations</span>
        </v-btn>

        <v-btn value="dashboard" :to="{ name: 'Dashboard' }" v-haptic>
            <v-icon>$profile</v-icon>

            <span>Dashboard</span>
        </v-btn>
    </v-bottom-navigation>
</template>

<script setup>
const rail = ref(true);
const drawer = ref(true);

const { name } = useDisplay();
const { orientation } = useScreenOrientation();

watchEffect(() => {
    if (name.value === "xs" && orientation.value === "portrait-primary") {
        drawer.value = false;
    } else drawer.value = true;
});

const { authStore, logout } = useAuth();
const { isConnected, isAdmin, user } = storeToRefs(authStore);
const { data: messageNotifs, execute: refreshNotifs } = useFetch("/conversation/notifs", { immediate: false });
const profilePictureUrl = ref("");

let refreshInterval;

onMounted(() => {
    // Si la barre est présente, on est forcément connecté
    refreshNotifs();
    refreshInterval = setInterval(refreshNotifs, 5000);
});

onUnmounted(() => {
    clearInterval(refreshInterval);
});

const router = useRouter();
watch(
    () => user.value?.imageUrl,
    (url) => {
        if (url) profilePictureUrl.value = "/cdn/pictures/" + url;
    },
    { immediate: true },
);

async function logoutClick() {
    await logout();
    router.push("/");
}

async function askForHelp() {
    const conversation = await http.post("/conversation/askForHelp");

    router.push({ name: "Messages", hash: `#${conversation.id}` });
}
</script>

<style scoped>
.v-navigation-drawer {
    &.standard {
        left: 1% !important;
        top: 1% !important;
        height: 98% !important;
    }
}

.v-list-item__spacer {
    width: 32px;
}

.image-full-enter-active {
    transition: all 0.75s cubic-bezier(0, 0.93, 0, 0.87);
}

.image-full-leave-active {
    transition: all 0.25s ease-out;
}

.image-ne-enter-active {
    transition-delay: 0.25s;
}

.image-full-enter-from,
.image-full-leave-to,
.image-ne-enter-from,
.image-ne-leave-to {
    opacity: 0;
}
</style>
